/* eslint-disable @angular-eslint/prefer-standalone */
import { Component, EventEmitter, Input, OnInit, Output, ViewChild, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Permissions } from '@app/core/guard/Permissions';
import { Roles } from '@app/core/guard/Roles';
import { Store } from '@ngrx/store';
import { ImpersonationSessionService } from '@services/auth/impersonation-session.service';
import { UtilsService } from '@services/helpers/utils.service';
import { selectFullName, selectUserId } from '@state/auth/auth.selectors';
import { AppState } from '@state/index';
import { ModalApiTokensComponent } from '@app/features/dashboard/user-list/components/modal-api-tokens/modal-api-tokens.component';
import { VaultBaseComponent } from '@app/shared/components/base/vault-base.component';
import { SortEvent, TableAction, TableActions } from '@app/shared/components/table/table.component';
import { User } from '@interfaces/a-organizar/user';
import { Pagination } from '@interfaces/common/pagination';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from '@services/helpers/confirmation.service';
import { DateFormatterService } from '@services/helpers/date-formatter.service';
import { PermissionsService } from '@services/helpers/permissions.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { UserService } from '@services/http/user.service';
import { ModalConfirmComponent } from '@src/app/shared/components/modal/modal-confirm/modal-confrm.component';
import { ModalOkComponent } from '@src/app/shared/components/modal/modal-ok/modal-ok.component';
import { ColumnDefinition } from '@src/app/shared/interfaces/column-definition.interface';
import { MenuItem } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { PaginatorState } from 'primeng/paginator';
import { combineLatest, take } from 'rxjs';
import { toSentenceCaseName } from '@src/app/core/utils/name-case.utils';

@Component({
  selector: 'app-user-list-content',
  templateUrl: './user-list-content.component.html',
  styleUrls: ['./user-list-content.component.scss'],
  standalone: false,
})
export class UserListContentComponent extends VaultBaseComponent implements OnInit {
  @ViewChild('apiTokensModal') apiTokensModalRef!: ModalApiTokensComponent;

  /** Primeira listagem em andamento: mantém a tabela visível para o esqueleto aparecer. */
  @Input() loading = false;
  @Input() userList: User[] = [];
  @Input() pagination: Pagination = {
    total: 0,
    currentPage: 0,
    pages: 0,
    itemsPerPage: 0,
  };
  @Output() sendTablePagination: EventEmitter<PaginatorState> = new EventEmitter<PaginatorState>();
  @Output() sendTableAction: EventEmitter<TableActions<User>> = new EventEmitter<
    TableActions<User>
  >();
  @Output() sendTableSort: EventEmitter<SortEvent> = new EventEmitter<SortEvent>();
  /** Texto da busca da tabela (a página filtra na API). */
  @Output() sendSearch = new EventEmitter<string>();
  /** Funil da tabela: a página abre o painel de filtros. */
  @Output() sendOpenFilter = new EventEmitter<void>();

  userPermissions = Permissions.User;
  columnDefinitions: ColumnDefinition[] = [];
  /** Ações do menu ⋯ da linha (card 3697): Editar e Tokens de API no topo, senha no meio, Inativar no fim. */
  rowActions: MenuItem[] = [];
  /** Menu de usuário inativo: só Editar (para consultar o cadastro). */
  inactiveRowActions: MenuItem[] = [];
  /** Com `User.UPDATE`, o nome na lista vira link para o cadastro do usuário. */
  canEdit = false;
  /** ADMIN com `UserImpersonation.IMPERSONATE` (card 3804). */
  canImpersonate = false;
  /** Quem está logado: a própria linha não oferece "Entrar como". */
  private currentUserId: string | null = null;
  /** Menu ⋯ de linha em que dá para "Entrar como este usuário". */
  rowActionsWithImpersonation: MenuItem[] = [];

  private readonly appStore = inject<Store<AppState>>(Store);
  private readonly utilsService = inject(UtilsService);
  private readonly impersonationSession = inject(ImpersonationSessionService);

  constructor(
    translateService: TranslateService,
    private router: Router,
    private confirmationService: ConfirmationService,
    private userService: UserService,
    private dialogService: DialogService,
    private toast: ToastHelperService,
    private permissionsService: PermissionsService,
    private dateFormatterService: DateFormatterService
  ) {
    super(translateService);
  }

  ngOnInit(): void {
    this.rowActions = this.buildRowActions(false, false);

    // Editar só com User.UPDATE, Inativar só com User.DELETE e Reativar só com User.REACTIVATE.
    // "Entrar como" (card 3804) só com UserImpersonation.IMPERSONATE e perfil ADMIN.
    combineLatest([
      this.permissionsService.hasPermission(this.userPermissions.UPDATE),
      this.permissionsService.hasPermission(this.userPermissions.DELETE),
      this.permissionsService.hasPermission(this.userPermissions.REACTIVATE),
      this.permissionsService.hasPermission(Permissions.UserImpersonation.IMPERSONATE),
      this.permissionsService.hasRole(Roles.ADMIN),
      this.appStore.select(selectUserId),
    ])
      .pipe(take(1))
      .subscribe(([canUpdate, canDelete, canReactivate, canImpersonate, isAdmin, userId]) => {
        this.canEdit = canUpdate;
        this.canImpersonate = canImpersonate && isAdmin;
        this.currentUserId = userId ?? null;
        this.rowActions = this.buildRowActions(canUpdate, canDelete);
        this.rowActionsWithImpersonation = this.withImpersonationAction(this.rowActions);
        // Inativo não abre no cadastro (a API só carrega ativos): reative antes de editar.
        this.inactiveRowActions = canReactivate
          ? [{ label: 'Reativar usuário', icon: 'pi pi-replay', action: 'reactivate' }]
          : [];
      });

    this.columnDefinitions = [
      new ColumnDefinition({
        field: 'name',
        header: this.t(this.tk.User.Name),
        sortable: true,
        sortField: 'firstName',
      }),
      new ColumnDefinition({
        field: 'email',
        header: this.t(this.tk.User.Email),
        sortable: true,
      }),
      new ColumnDefinition({
        field: 'roles',
        header: this.t(this.tk.User.Profile),
        sortable: false,
      }),
      new ColumnDefinition({
        field: 'status',
        header: 'Status',
        sortable: false,
      }),
      new ColumnDefinition({
        field: 'company',
        header: 'Empresa',
        sortable: true,
        sortField: 'company.corporateName',
      }),
      new ColumnDefinition({
        field: 'tenant',
        header: 'Tenant',
        sortable: true,
        sortField: 'tenant.name',
      }),
      new ColumnDefinition({
        field: 'lastLogin',
        header: this.t(this.tk.User.LastLogin),
        sortable: true,
      }),
      new ColumnDefinition({
        field: 'hasPassword',
        header: 'Senha',
        sortable: false,
      }),
    ];
  }

  /** Nome de exibição do usuário: `name` quando vem pronto, senão nome + sobrenome. */
  displayName(user: User): string {
    const name = (user.name || [user.firstName, user.surName].filter(Boolean).join(' ')).trim();
    // Usuário sem nome (ex.: contas desativadas em massa) aparece pelo e-mail em vez de linha vazia.
    return name || user.email || '';
  }

  /** Iniciais do avatar da linha (primeira letra do nome e do sobrenome). */
  initials(user: User): string {
    const parts = this.displayName(user).trim().split(/\s+/).filter(Boolean);
    const first = parts[0]?.[0] ?? '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (first + last).toUpperCase() || '?';
  }

  /**
   * Cor da etiqueta do perfil. O nome do perfil vem do backend (ADMIN, ADMIN TENANT, COMPANY,
   * PARTNER...); perfil desconhecido fica neutro em vez de quebrar.
   */
  roleTagClass(roleName: string): string {
    const name = (roleName || '').toUpperCase();
    if (name.includes('TENANT')) return 'user-list-content__role--tenant';
    if (name.includes('ADMIN')) return 'user-list-content__role--admin';
    if (name.includes('PARTNER') || name.includes('PARCEIRO')) {
      return 'user-list-content__role--partner';
    }
    if (name.includes('COMPANY') || name.includes('EMPRESA')) {
      return 'user-list-content__role--company';
    }
    return '';
  }

  /** Usuário inativado (`deletedAt` preenchido). */
  isInactive(user: User): boolean {
    return !!user.deletedAt;
  }

  /** Menu ⋯ da linha: inativo só tem Reativar (os arrays são fixos, para o menu não ser recriado a cada checagem). */
  menuFor(user: User): MenuItem[] {
    if (this.isInactive(user)) return this.inactiveRowActions;
    return this.canImpersonateUser(user) ? this.rowActionsWithImpersonation : this.rowActions;
  }

  /**
   * "Entrar como este usuário" (card 3804) aparece só para ADMIN com a permissão, nunca na própria linha nem em linha
   * de ADMIN (o backend recusa os dois de qualquer jeito; aqui só evita oferecer o que não funciona).
   */
  canImpersonateUser(user: User): boolean {
    if (!this.canImpersonate || !user?.id || user.id === this.currentUserId) return false;
    return !(user.roles ?? []).some(
      role => (role?.name ?? '').trim().toUpperCase() === Roles.ADMIN
    );
  }

  /** Menu com "Entrar como este usuário" logo depois dos itens de acesso (antes de Inativar). */
  private withImpersonationAction(actions: MenuItem[]): MenuItem[] {
    const item: MenuItem = {
      label: 'Entrar como este usuário',
      icon: 'pi pi-sign-in',
      action: 'impersonate',
    };
    const dangerStart = actions.findIndex(action => action['action'] === 'delete');
    if (dangerStart < 0) return [...actions, { separator: true }, item];
    // O separador antes de Inativar fica em dangerStart - 1: o item entra antes dele.
    return [
      ...actions.slice(0, dangerStart - 1),
      { separator: true },
      item,
      ...actions.slice(dangerStart - 1),
    ];
  }

  /**
   * Confirma e entra como o usuário da linha (card 3804). A confirmação avisa que as outras abas saem; recusa do
   * backend aparece no toast com a mensagem dele, e a sessão do ADMIN continua como estava.
   */
  handleImpersonateAction(user: User): void {
    // Caixa de frase para exibir: "VICTOR MARTINS" vira "Victor Martins".
    const name = toSentenceCaseName(this.displayName(user));
    this.confirmationService
      .confirm(
        `Entrar como ${name}?`,
        `As outras abas do Cognito abertas neste navegador vão sair. Você entra como ${name} por até 60 minutos e volta para a sua conta pelo menu do seu nome. A entrada fica registrada no histórico em seu nome; o que você fizer lá aparece como feito por ${name}.`,
        'default',
        'Cancelar',
        'Entrar como este usuário'
      )
      .subscribe(confirmed => {
        if (!confirmed) return;
        this.appStore
          .select(selectFullName)
          .pipe(take(1))
          .subscribe(adminName => void this.enterAs(user, adminName));
      });
  }

  /**
   * Pede a sessão do usuário e troca a aba para ela. Em sucesso a página recarrega; aqui só trata a recusa.
   * @param user Usuário alvo.
   * @param adminName Nome de quem está entrando.
   */
  private async enterAs(user: User, adminName: string): Promise<void> {
    this.utilsService.setIsLoading(true);
    try {
      await this.impersonationSession.enterAs(user.id, adminName);
    } catch (error: unknown) {
      this.utilsService.setIsLoading(false);
      const backendMessage = (error as { errors?: { message?: string }[] })?.errors?.[0]?.message;
      this.toast.showToastError(
        `Não foi possível entrar como ${toSentenceCaseName(this.displayName(user))}`,
        backendMessage ||
          (error instanceof Error ? error.message : '') ||
          'Tente de novo em instantes. Se continuar, avise o time de tecnologia.'
      );
    }
  }

  /** Esmaece a linha do usuário inativo. */
  readonly rowClass = (user: User): string =>
    this.isInactive(user) ? 'user-list-content__row--inactive' : '';

  /**
   * Situação da senha para a coluna Senha: definida, sem senha ou com o link de criação vencido (sem senha e
   * validade do último link no passado). Só exibição; a regra do filtro mora no backend.
   */
  passwordState(user: User): { icon: string; tone: 'ok' | 'pending' | 'expired'; tooltip: string } {
    if (user.hasPassword) {
      return { icon: 'pi-check-circle', tone: 'ok', tooltip: 'Senha definida' };
    }
    const validUntil = this.dateFormatterService.parseDate(user.passwordLinkValidUntil);
    if (validUntil && validUntil.getTime() < this.dateFormatterService.getNowTimestamp()) {
      return {
        icon: 'pi-exclamation-circle',
        tone: 'expired',
        tooltip: `Link de senha vencido em ${this.dateFormatterService.formatDateTimeInstant(validUntil)}. Envie um novo pelo menu ⋯.`,
      };
    }
    return { icon: 'pi-clock', tone: 'pending', tooltip: 'O usuário ainda não criou a senha' };
  }

  /** Ação escolhida no menu ⋯ da linha: segue o mesmo caminho do menu antigo. */
  emitRowAction(action: string, user: User): void {
    this.handleTableAction({ action, item: user } as TableAction<User>);
  }

  /** Itens do menu ⋯, agrupados por assunto e filtrados pelas permissões de quem vê a lista. */
  private buildRowActions(canUpdate: boolean, canDelete: boolean): MenuItem[] {
    const access: MenuItem[] = [
      ...(canUpdate ? [{ label: 'Editar usuário', icon: 'pi pi-pencil', action: 'edit' }] : []),
      { label: 'Tokens de API', icon: 'pi pi-key', action: 'manage-api-tokens' },
      { separator: true },
      {
        label: this.t(this.tk.Management.PasswordRecoveryLink),
        icon: 'pi pi-link',
        action: 'link-create-password',
      },
      {
        label: this.t(this.tk.Management.SendEmailPasswordRecovery),
        icon: 'pi pi-envelope',
        action: 'email-recover-password',
      },
    ];
    if (!canDelete) return access;
    return [
      ...access,
      { separator: true },
      {
        label: 'Inativar usuário',
        icon: 'pi pi-ban',
        styleClass: 'user-list-content__danger-item',
        action: 'delete',
      },
    ];
  }

  addUser(): void {
    this.router.navigate(['dashboard/user/register']);
  }

  handleTableAction(tableAction: TableAction<User>) {
    if (tableAction.action === 'edit') {
      this.router.navigate(['dashboard/user/register', tableAction.item.id]);
    }

    if (tableAction.action === 'delete') {
      this.handleDeleteAction(tableAction.item);
    }

    if (tableAction.action === 'reactivate') {
      this.handleReactivateAction(tableAction.item);
    }

    if (tableAction.action === 'impersonate') {
      this.handleImpersonateAction(tableAction.item);
    }

    if (tableAction.action === 'link-create-password') {
      this.userService.getResetPasswordLink(tableAction.item.id).subscribe(passwordLink => {
        this.dialogService.open(ModalOkComponent, {
          data: {
            description: passwordLink,
            title: this.t(this.tk.Auth.PasswordRecoveryLink1),
          },
        });
      });
    }

    if (tableAction.action === 'email-recover-password') {
      const modalRef = this.dialogService.open(ModalConfirmComponent, {
        data: {
          title: this.t(this.tk.Auth.RecoverPasswordTitle),
          description: this.t(this.tk.Auth.ConfirmSendPasswordRecoveryEmail),
          btnFirst: this.t(this.tk.General.Cancel),
          btnSecond: this.t(this.tk.General.Confirm),
        },
      });

      if (modalRef) {
        modalRef.onClose.subscribe({
          next: result => {
            if (result.confirmed) {
              this.userService.SendEmailResetPassword(tableAction.item.id).subscribe(() => {
                this.toast.showToastSuccess(this.t(this.tk.General.EmailSentSuccessfully));
              });
            }
          },
        });
      }
    }

    if (tableAction.action === 'manage-api-tokens') {
      this.openApiTokensModal(tableAction.item);
    }
  }

  handleTablePagination(pagination: PaginatorState) {
    this.sendTablePagination.emit(pagination);
  }

  handleTableSort(event: SortEvent) {
    this.sendTableSort.emit(event);
  }

  /**
   * Confirma e emite a exclusão do usuário.
   *
   * 🐞 Usava o `ConfirmDialogService`, que publica no `ConfirmationService` do PrimeNG com a
   * chave `default-dialog`. O `p-confirmDialog` que escutava essa chave **não existe**: o
   * global saiu do `app.component.html` em 26/07/2024, e os que sobraram no projeto estão
   * dentro de telas e modais específicos, cada um com a sua própria chave. O PrimeNG casa a
   * mensagem por `confirmation.key === this.key`, então a confirmação era emitida para
   * ninguém: a modal não abria, a `Promise` nunca resolvia, nenhuma requisição saía e **nada
   * aparecia no console**. O clique em "Deletar" simplesmente não fazia nada.
   *
   * Agora usa o `ConfirmationService` do projeto — o mesmo caminho das listas irmãs
   * (Empresas, Grupos, Tenants) e a régua do guia do frontend —, que monta a modal pelo
   * `DialogService` e não depende de nenhum componente montado na página.
   */
  handleDeleteAction(item: User) {
    // "Excluir" virou "Inativar" (card 3697): a exclusão sempre foi lógica (DeletedAt); o usuário continua
    // na lista como Inativo e o login passa a recusá-lo.
    this.confirmationService
      .confirm(
        `Inativar ${this.displayName(item)}?`,
        'A pessoa deixa de entrar na plataforma e passa a aparecer como Inativo na lista. O histórico dela é mantido.',
        'danger',
        'Cancelar',
        'Inativar usuário'
      )
      .subscribe(confirmed => {
        if (confirmed) {
          this.sendTableAction.emit({ action: 'delete', items: [item] });
        }
      });
  }

  /** Confirma e emite a reativação do usuário inativado (card 3697). */
  handleReactivateAction(item: User) {
    this.confirmationService
      .confirm(
        `Reativar ${this.displayName(item)}?`,
        'A pessoa volta a entrar na plataforma com o mesmo perfil e as mesmas empresas. Tokens de API revogados na inativação continuam revogados; gere novos se precisar.',
        'default',
        'Cancelar',
        'Reativar usuário'
      )
      .subscribe(confirmed => {
        if (confirmed) {
          this.sendTableAction.emit({ action: 'reactivate', items: [item] });
        }
      });
  }

  /**
   * Texto do tooltip da coluna Empresa para usuário de grupo econômico ou multiempresa: o título
   * (grupo ou "Acesso a N empresas") e uma empresa por linha, na ordem alfabética do backend.
   * Lista longa é cortada com aviso, porque o tooltip do PrimeNG não rola.
   */
  scopeCompaniesTooltip(user: User): string {
    const names = (user.scopeCompanies ?? []).map(company => company.name).filter(Boolean);
    const title = user.economicGroup
      ? `Grupo ${user.economicGroup.name} (${names.length} ${names.length === 1 ? 'empresa' : 'empresas'})`
      : `Acesso a ${names.length} ${names.length === 1 ? 'empresa' : 'empresas'}`;
    if (!names.length) {
      return title;
    }

    const maxLines = 20;
    const lines = names.slice(0, maxLines).map(name => `• ${name}`);
    if (names.length > maxLines) {
      lines.push(`… e mais ${names.length - maxLines}`);
    }
    return `${title}\n${lines.join('\n')}`;
  }

  openApiTokensModal(user: User) {
    this.apiTokensModalRef.user = user;
    this.apiTokensModalRef.open(user.id);
  }

  onApiTokensActionCompleted() {
    // TODO: Implementar lógica quando a modal de API tokens for fechada
  }
}
